import date from "date-and-time";

import { useTranslation } from "react-i18next";

import Card from "../shared/Card/Card";

import useLeadMessages from "../../data/useLeadMessages";

import { ILead } from "../../models/Lead";

import classNames from "../../utils/classNames";
import { IMessage, MessageStatus } from "../../models/Message";
import LoadingWheel from "../shared/LoadingWheel";
import { Fragment, useEffect, useRef, useState } from "react";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useNotification from "../notifications/useNotifications";

// The thread is the contact's whole history, so it mixes in texts sent from their other leads.
// Anything not belonging to the lead being viewed gets an indicator naming the vehicle it came
// from, so a text about a different car is not mistaken for one about this one.
function OtherLeadTag({ message }: { message: IMessage }) {
  const { t } = useTranslation("leads");

  const label = message.leadVrm ?? t("another lead");
  const title = message.leadId
    ? t("Sent from lead {{vrm}}", { vrm: label })
    : t("Not linked to a lead");

  return (
    <span
      title={title}
      className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-500 ring-1 ring-inset ring-gray-300"
    >
      {message.leadId ? label : t("No lead")}
    </span>
  );
}

function SMSBubble({
  message,
  currentLeadId,
}: {
  message: IMessage;
  currentLeadId?: string;
}) {
  const { t } = useTranslation("leads");

  const inbound =
    message.status === MessageStatus.Received ||
    message.status === MessageStatus.Read;

  // Inbound texts arrive against the contact, so they have no lead of their own to disagree with.
  const fromOtherLead = !inbound && message.leadId !== currentLeadId;

  return (
    <div>
      <div
        className={classNames(
          "rounded-lg p-2 px-3 whitespace-pre-line",
          inbound ? "bg-gray-200 text-black" : "bg-primary-500 text-white"
        )}
      >
        {message.body}
      </div>
      <div className="pt-1 px-2 text-xs text-gray-400 flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <span>
          {date.format(new Date(message.timestamp), "DD/MM/YY HH:mm")} -{" "}
          {t(MessageStatus[message.status])}
        </span>
        {message.isAutomated && (
          <span
            title={
              message.templateName
                ? t("Sent automatically by the system ({{template}})", {
                    template: message.templateName,
                  })
                : t("Sent automatically by the system")
            }
            className="inline-flex items-center rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-700 ring-1 ring-inset ring-amber-200"
          >
            {t("Automated")}
          </span>
        )}
        {fromOtherLead && <OtherLeadTag message={message} />}
      </div>
    </div>
  );
}

export default function LeadSMS({ lead }: { lead: ILead }) {
  const { t } = useTranslation("leads");
  const { leadMessages, sendMessage, markAsRead } = useLeadMessages(lead.contactId!, "LeadSMS");
  const { saveHandlers } = useDefaultCRUDHandlers("Text");
  const { addNotification } = useNotification();
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [messageText, setMessageText] = useState("");

  const messagesEndRef = useRef<null | HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 500);

    if (!leadMessages.isLoading && leadMessages.data) {
      //if any message is unread, mark it as read
      let unreadMessages = leadMessages.data.data.filter(
        (m) => m.status === MessageStatus.Received
      );

      if (unreadMessages.length > 0) {
        const messageIds = unreadMessages.map((m) => m.id);

        let data = {
          messages: messageIds
        };
        markAsRead.mutate(data);
      }

      setMessages(leadMessages.data.data);
    }
  }, [leadMessages.isLoading, leadMessages.data])

  useEffect(() => {
    setTimeout(() => {
      scrollToBottom();
    }, 500);
  }, [setMessages])

  const handleSend = () => {
    let data = {
      contactId: lead.contactId,
      leadId: lead.id,
      recipient: lead.contact?.telephone1,
      body: messageText,
    };

    let newMessage: IMessage = {
      id: "0",
      userId: lead.contactId!,
      recipient: lead.contact?.telephone1!,
      body: messageText,
      status: MessageStatus.Waiting,
      timestamp: new Date().toISOString(),
      sid: "",
      from: "",
      created: new Date().toISOString(),
      updated: null,
      leadId: lead.id,
      leadVrm: lead.vehicle?.vrm ?? null,
      sentBy: "",
      isAutomated: false,
      templateId: null,
      templateName: null
    };

    sendMessage.mutate(data, {
      onSuccess: (res: any) => {
        // The API answers 200 with Success: false when the send is rejected - an invalid number,
        // no SMS credit, Twilio refusing it. Appending the bubble regardless told the user their
        // text had gone when nothing was ever sent and no message was stored.
        if (res?.success === false) {
          addNotification({
            variant: "error",
            primaryText: t("Text not sent"),
            secondaryText: res.message,
          });
          return;
        }

        setMessageText("");
        setMessages([...messages, newMessage]);
        setTimeout(() => {
          scrollToBottom();
        }, 500);
        saveHandlers.onSuccess();
      },
      onError: saveHandlers.onError
    });
  };

  return (
    <Card title={t("SMS Conversation")}>
      {leadMessages.isLoading ? (
        <LoadingWheel />
      ) : (
        <div className="divide-y">
          <div className="max-h-[32rem] p-3 grid grid-cols-2 gap-x-16 gap-y-2 overflow-y-auto">
            {messages.map((message, i) => {
              let padLeft = true;

              if (message.status === MessageStatus.Received) {
                padLeft = false;
              }

              if (message.status === MessageStatus.Read) {
                padLeft = false;
              }

              return (
                <Fragment key={message.id === "0" ? `pending-${i}` : message.id}>
                  {padLeft && <div />}
                  <SMSBubble message={message} currentLeadId={lead.id} />
                  {!padLeft && <div />}
                </Fragment>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
          <div className="h-32 p-3 grid grid-cols-8 gap-x-4">
            <div className="col-span-7">
              <textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                className="w-full h-full resize-none"
                placeholder={t("Enter message text here...")}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={handleSend}
                className="w-full rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
                type="button"
              >
                {t("Send")}
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
